import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { backendConfig } from '../config/backendConfig';

export default function MentorRoute({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (loading) return; // wait for auth flag once

      // If we have a mentor session (email+ownerId), allow mentor pages without Firebase auth
      let session = null;
      try { session = JSON.parse(localStorage.getItem('mentor_session') || '{}'); } catch (_) {}
      const hasSession = !!(session && session.ownerId && session.email);

      try {
        let isMentor = false;
        if (isAuthenticated && user) {
          // 1) Check user account type for mentor (if signed in)
          const snap = await getDoc(doc(db, 'users', user.uid));
          const data = snap.exists() ? snap.data() : {};
          const acctType = (data.accountType || '').toString();
          isMentor = acctType === 'ngo_admin' || acctType === 'art_mentor' || acctType === 'mentor';
        }
        // If we have a mentor session, treat as mentor regardless of Firebase login
        if (hasSession) isMentor = true;

        if (!isMentor) {
          navigate('/membership');
          setAllowed(false);
          setChecking(false);
          return;
        }

        // 2) Check if mentor profile exists on backend; if not, go to onboarding
        // BUT: Skip this check for single mentors who just signed up (they don't have backend profiles yet)
        const isSingleMentor = user?.mentorType === 'single';
        
        if (!isSingleMentor) {
          const ownerId = hasSession ? session.ownerId : (user?.uid || '');
          const email = hasSession ? session.email : (user?.email || '');
          const params = new URLSearchParams({ ownerId, email }).toString();
          const resp = await fetch(`${backendConfig.baseUrl}/api/mentors/profile?${params}`);
          if (resp.ok) {
            const j = await resp.json().catch(()=>({}));
            if (!j.exists) {
              // First-time mentor: store a lightweight session and redirect
              try { localStorage.setItem('mentor_session', JSON.stringify({ ownerId, email })); } catch (_) {}
              navigate('/mentors/onboarding');
              setAllowed(false);
              setChecking(false);
              return;
            } else if (j.profile && j.profile.status === 'complete') {
              // Profile complete -> allow dashboard
              setAllowed(true);
              setChecking(false);
              return;
            }
          }
        }

        // Profile exists → allow dashboard/children
        setAllowed(true);
      } catch (_) {
        // On error, be safe and send to onboarding flow
        try { localStorage.setItem('mentor_session', JSON.stringify({ ownerId: user.uid, email: user.email || '' })); } catch (_) {}
        navigate('/mentors/onboarding');
        setAllowed(false);
      } finally {
        setChecking(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAuthenticated, loading]);

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200">
        <div className="text-amber-700">Loading…</div>
      </div>
    );
  }

  return allowed ? children : null;
}

